import React, { useState, useEffect, useRef } from 'react';
import { Plus, Shuffle, Trash2, X, ChevronUp, ChevronDown, Clock, Upload, Scale } from 'lucide-react';
import { useLang } from '../utils/i18n';
import type { WheelEntry } from '../types/wheel';
import type { ResultEntry } from '../utils/results';
import { PALETTES, type PaletteName } from '../utils/palettes';
import ImportModal from './ImportModal';
import ImageUploadButton from './ImageUploadButton';
import { MAX_ENTRY_NAME_LENGTH } from '../utils/wheelData';
import { MAX_ENTRIES } from '../utils/limits';
import { clampWeight, DEFAULT_WEIGHT, isUniform, MAX_WEIGHT, MIN_WEIGHT, probabilities } from '../utils/weights';

type Tab = 'entries' | 'results';

interface MobileEntriesPanelProps {
  entries: WheelEntry[];
  results: ResultEntry[];
  onAdd: (name: string) => void;
  onAddImageEntries: (entries: WheelEntry[]) => void;
  onWeightChange: (index: number, weight: number) => void;
  onRemove: (index: number) => void;
  onShuffle: () => void;
  onSort: () => void;
  onClear: () => void;
  onImportEntries: (entries: { name: string; weight: number }[]) => void;
  isSpinning: boolean;
  palette: PaletteName;
  /** Controlled tab — lets the parent switch to Results after a spin. */
  activeTab?: Tab;
  onTabChange?: (tab: Tab) => void;
}

const MobileEntriesPanel: React.FC<MobileEntriesPanelProps> = ({
  entries,
  results,
  onAdd,
  onAddImageEntries,
  onWeightChange,
  onRemove,
  onShuffle,
  onSort,
  onClear,
  onImportEntries,
  isSpinning,
  palette,
  activeTab: controlledTab,
  onTabChange,
}) => {
  const COLORS = PALETTES[palette] ?? PALETTES.classic;
  const { t } = useLang();
  const [internalTab, setInternalTab] = useState<Tab>('entries');
  const [inputValue, setInputValue] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const weights = entries.map(entry => entry.weight ?? DEFAULT_WEIGHT);
  const weighted = !isUniform(weights);
  const probs = probabilities(weights);
  const [showWeights, setShowWeights] = useState(() => weighted);

  // A shared/imported weighted wheel should never arrive with its odds hidden.
  useEffect(() => {
    if (weighted) setShowWeights(true);
  }, [weighted]);

  // Support both controlled and uncontrolled tab.
  const activeTab = controlledTab ?? internalTab;
  const setActiveTab = (tab: Tab) => {
    setInternalTab(tab);
    onTabChange?.(tab);
  };

  // Sync controlled tab changes from parent.
  useEffect(() => {
    if (controlledTab !== undefined) {
      setInternalTab(controlledTab);
    }
  }, [controlledTab]);

  function handleAdd() {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    setInputValue('');
    inputRef.current?.focus();
  }

  const handleImport = (imported: { name: string; weight: number }[]) => {
    onImportEntries(imported);
  };

  return (
    <section
      aria-label={t('entries.title')}
      className="w-full bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden"
    >
      {/* ── TAB BAR ── */}
      <div className="flex border-b border-gray-100 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('entries')}
          className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider transition-colors relative ${
            activeTab === 'entries'
              ? 'text-violet-600 dark:text-violet-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
          }`}
        >
          {t('entries.title')}
          {entries.length > 0 && (
            <span
              className={`ms-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none ${
                activeTab === 'entries'
                  ? 'bg-violet-600 text-white'
                  : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
              }`}
            >
              {entries.length}
            </span>
          )}
          {activeTab === 'entries' && (
            <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-violet-500 rounded-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('results')}
          className={`flex-1 py-3 text-xs font-bold uppercase tracking-wider transition-colors relative ${
            activeTab === 'results'
              ? 'text-violet-600 dark:text-violet-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
          }`}
        >
          {t('entries.results')}
          {results.length > 0 && (
            <span
              className={`ms-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none ${
                activeTab === 'results'
                  ? 'bg-violet-600 text-white'
                  : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
              }`}
            >
              {results.length}
            </span>
          )}
          {activeTab === 'results' && (
            <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-violet-500 rounded-full" />
          )}
        </button>
      </div>

      {/* ── ENTRIES TAB ── */}
      {activeTab === 'entries' && (
        <>
          {/* Action buttons */}
          <div className="flex gap-2 px-3 py-2.5 border-b border-gray-50 dark:border-gray-800">
            <button
              onClick={onShuffle}
              disabled={isSpinning || entries.length < 2}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Shuffle size={12} /> {t('entries.shuffle')}
            </button>
            <button
              onClick={onSort}
              disabled={isSpinning || entries.length < 2}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronUp size={11} className="-mb-0.5" /><ChevronDown size={11} className="-mt-0.5 -ms-1.5" /> {t('entries.sort')}
            </button>
            <button
              onClick={onClear}
              disabled={isSpinning || entries.length === 0}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors ms-auto"
            >
              <Trash2 size={12} /> {t('entries.clear')}
            </button>
          </div>

          {/* Entry list — scrollable */}
          <ul
            role="list"
            className="divide-y divide-gray-50 dark:divide-gray-800 overflow-y-auto"
            style={{ maxHeight: '260px' }}
          >
            {entries.length === 0 && (
              <li className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                {t('entries.empty')}
                <p className="text-xs text-gray-300 dark:text-gray-600 mt-1">{t('entries.emptySub')}</p>
              </li>
            )}
            {entries.map((entry, i) => (
              <li
                key={i}
                className="group flex items-center gap-2.5 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                {/* Colour dot */}
                <span
                  aria-hidden="true"
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: entry.color ?? COLORS[i % COLORS.length] }}
                />
                {entry.imageDataUrl && (
                  <img
                    src={entry.imageDataUrl}
                    alt=""
                    className="h-8 w-8 flex-shrink-0 rounded-lg object-cover ring-1 ring-gray-200 dark:ring-gray-700"
                    onError={event => { event.currentTarget.hidden = true; }}
                  />
                )}
                <span dir="auto" className="flex-1 min-w-0 text-sm text-gray-700 dark:text-gray-200 truncate">
                  {entry.name}
                </span>
                {showWeights ? (
                  <div className="flex flex-shrink-0 items-center gap-1">
                    {weighted && (
                      <span className="rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-bold leading-none text-purple-600 dark:bg-purple-900/40 dark:text-purple-400">
                        {probs[i].toFixed(probs[i] < 10 ? 1 : 0)}%
                      </span>
                    )}
                    <input
                      type="number"
                      min={MIN_WEIGHT}
                      max={MAX_WEIGHT}
                      step={0.5}
                      value={entry.weight ?? DEFAULT_WEIGHT}
                      disabled={isSpinning}
                      onChange={(event) => onWeightChange(i, clampWeight(event.target.value))}
                      aria-label={t('entries.weightFor', { name: entry.name })}
                      title={t('entries.weightHelp')}
                      className="w-12 rounded border border-gray-200 bg-white text-center text-xs text-gray-700 outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-400 disabled:opacity-40 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                    />
                  </div>
                ) : entry.weight !== undefined && entry.weight !== DEFAULT_WEIGHT && (
                  <span className="text-[10px] text-gray-500 dark:text-gray-400 font-mono flex-shrink-0">
                    ×{entry.weight}
                  </span>
                )}
                {/* Remove button */}
                <button
                  onClick={() => onRemove(i)}
                  disabled={isSpinning}
                  aria-label={t('entries.removeNamed', { name: entry.name })}
                  className="flex-shrink-0 p-1 rounded-md text-gray-300 dark:text-gray-600 hover:text-red-600 dark:hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-all disabled:pointer-events-none"
                >
                  <X size={13} />
                </button>
              </li>
            ))}
          </ul>

          {/* Add entry input */}
          <div className="flex gap-2 p-3 border-t border-gray-100 dark:border-gray-800">
            <label htmlFor="mobile-add-entry" className="sr-only">
              {t('entries.add')}
            </label>
            <input
              id="mobile-add-entry"
              ref={inputRef}
              type="text"
              maxLength={MAX_ENTRY_NAME_LENGTH}
              value={inputValue}
              dir="auto"
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              placeholder={t('entries.placeholder')}
              disabled={isSpinning}
              className="min-w-0 flex-1 px-3 py-2 text-sm border border-gray-500 dark:border-gray-400 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent disabled:opacity-50 transition-all"
            />
            <button
              onClick={handleAdd}
              disabled={isSpinning || !inputValue.trim()}
              aria-label={t('entries.add')}
              className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm font-semibold"
            >
              <Plus size={16} />
            </button>
            <ImageUploadButton
              disabled={isSpinning || entries.length >= MAX_ENTRIES}
              remainingEntries={Math.max(0, MAX_ENTRIES - entries.length)}
              existingImageDataLength={entries.reduce((sum, entry) => sum + (entry.imageDataUrl?.length ?? 0), 0)}
              onAddEntries={onAddImageEntries}
              compact
            />
          </div>

          {/* Bulk actions — collapsed by default */}
          <div className="px-3 pb-3">
            <button
              onClick={() => setBulkOpen((v) => !v)}
              className="w-full text-xs text-gray-500 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 flex items-center justify-center gap-1 py-1 transition-colors"
            >
              {bulkOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              {t('entries.moreOptions')}
            </button>
            {bulkOpen && (
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setShowWeights(value => !value)}
                  disabled={isSpinning || entries.length === 0}
                  aria-pressed={showWeights}
                  className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 py-2 text-xs font-semibold text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 hover:bg-violet-100 dark:hover:bg-violet-900/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Scale size={12} /> {t(showWeights ? 'entries.hideWeights' : 'entries.weights')}
                </button>
                <button
                  onClick={() => setShowImport(true)}
                  disabled={isSpinning}
                  className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 py-2 text-xs font-semibold text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 hover:bg-violet-100 dark:hover:bg-violet-900/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Upload size={12} /> {t('entries.import')}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── RESULTS TAB ── */}
      {activeTab === 'results' && (
        <div className="overflow-y-auto" style={{ maxHeight: '320px' }}>
          {results.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <Clock size={28} className="text-gray-200 dark:text-gray-600 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{t('entries.noResults')}</p>
              <p className="text-xs text-gray-300 dark:text-gray-600 mt-1">{t('entries.noResultsSub')}</p>
            </div>
          ) : (
            <ul role="list" className="divide-y divide-gray-50 dark:divide-gray-800">
              {[...results].reverse().map((r, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <span className="w-6 h-6 rounded-full bg-violet-50 dark:bg-violet-900/30 text-violet-500 dark:text-violet-400 flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {results.length - i}
                  </span>
                  <span dir="auto" className="flex-1 min-w-0 text-sm font-medium text-gray-800 dark:text-white truncate">
                    {r.name}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                    {r.time}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showImport && (
        <ImportModal onClose={() => setShowImport(false)} onImport={handleImport} />
      )}
    </section>
  );
};

export default MobileEntriesPanel;



